import React, { useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  Radio,
  RadioGroup,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { FiCheckCircle, FiXCircle } from "react-icons/fi";

const SOURCE_LABEL = {
  "the-hindu": "The Hindu",
  "indian-express": "Indian Express",
};

/**
 * MCQ runner for a Current Affairs quiz. Lets the user answer all questions,
 * submit for instant scoring, and review correct answers + explanations +
 * the article snippet each answer is grounded in.
 *
 * @param {{ quiz: object, onSubmit: (answers) => Promise<object>, submitting: boolean }} props
 */
export default function CaQuizRunner({ quiz, onSubmit, submitting }) {
  const [answers, setAnswers] = useState({}); // questionId -> selectedIndex
  const cardBg = useColorModeValue("white", "gray.800");
  const border = useColorModeValue("gray.200", "gray.600");
  const muted = useColorModeValue("gray.600", "gray.400");
  const correctBg = useColorModeValue("green.50", "green.900");
  const wrongBg = useColorModeValue("red.50", "red.900");
  const snippetBg = useColorModeValue("purple.50", "purple.900");

  const graded = quiz?.status === "attempted";
  const questions = quiz?.questions || [];

  const answeredCount = useMemo(
    () => Object.values(answers).filter((v) => v != null).length,
    [answers]
  );

  const handleSubmit = () => {
    const payload = questions.map((q) => ({
      questionId: q._id,
      selectedIndex: answers[q._id] != null ? Number(answers[q._id]) : null,
    }));
    onSubmit(payload);
  };

  if (!questions.length) return null;

  return (
    <Box>
      {graded ? (
        <HStack
          mb={4}
          p={3}
          borderRadius="lg"
          bg={cardBg}
          borderWidth="1px"
          borderColor={border}
          justify="space-between"
        >
          <Heading size="sm">Your score</Heading>
          <Badge
            colorScheme={quiz.score / quiz.total >= 0.5 ? "green" : "red"}
            fontSize="md"
            px={3}
            py={1}
            borderRadius="full"
          >
            {quiz.score} / {quiz.total} ·{" "}
            {Math.round((quiz.score / quiz.total) * 100)}%
          </Badge>
        </HStack>
      ) : null}

      <VStack align="stretch" spacing={3}>
        {questions.map((q, qi) => {
          const selected = graded ? q.selectedIndex : answers[q._id];
          return (
            <Box
              key={String(q._id)}
              bg={cardBg}
              borderWidth="1px"
              borderColor={border}
              borderRadius="xl"
              p={4}
              boxShadow="sm"
            >
              <HStack justify="space-between" align="start" mb={2}>
                <Text fontWeight="semibold" fontSize="sm">
                  {qi + 1}. {q.questionText}
                </Text>
                {graded ? (
                  <Icon
                    as={q.isCorrect ? FiCheckCircle : FiXCircle}
                    color={q.isCorrect ? "green.500" : "red.500"}
                    boxSize={5}
                    flexShrink={0}
                  />
                ) : null}
              </HStack>
              {q.articleTitle ? (
                <Text fontSize="xs" color={muted} mb={2} noOfLines={1}>
                  {SOURCE_LABEL[q.source] || q.source
                    ? `${SOURCE_LABEL[q.source] || q.source} · `
                    : ""}
                  {q.articleTitle}
                </Text>
              ) : null}

              <RadioGroup
                value={selected != null ? String(selected) : ""}
                onChange={(val) =>
                  !graded && setAnswers((prev) => ({ ...prev, [q._id]: Number(val) }))
                }
                isDisabled={graded}
              >
                <VStack align="stretch" spacing={1.5}>
                  {q.options.map((opt, oi) => {
                    let bg = "transparent";
                    if (graded) {
                      if (oi === q.correctIndex) bg = correctBg;
                      else if (oi === q.selectedIndex) bg = wrongBg;
                    }
                    return (
                      <Box key={oi} px={2} py={1.5} borderRadius="md" bg={bg}>
                        <Radio value={String(oi)} colorScheme="purple" size="sm">
                          <Text fontSize="sm">{opt}</Text>
                        </Radio>
                      </Box>
                    );
                  })}
                </VStack>
              </RadioGroup>

              {graded ? (
                <Box mt={3}>
                  {q.explanation ? (
                    <Text fontSize="sm" color={muted} mb={2}>
                      <b>Why:</b> {q.explanation}
                    </Text>
                  ) : null}
                  {q.sourceSnippet ? (
                    <Box bg={snippetBg} borderRadius="md" px={3} py={2}>
                      <Text fontSize="xs" color={muted}>
                        From the article: “{q.sourceSnippet}”
                      </Text>
                    </Box>
                  ) : null}
                </Box>
              ) : null}
            </Box>
          );
        })}
      </VStack>

      {!graded ? (
        <Flex mt={4} justify="space-between" align="center">
          <Text fontSize="sm" color={muted}>
            {answeredCount} / {questions.length} answered
          </Text>
          <Button
            colorScheme="purple"
            onClick={handleSubmit}
            isLoading={submitting}
            isDisabled={answeredCount === 0}
          >
            Submit answers
          </Button>
        </Flex>
      ) : null}
    </Box>
  );
}
